import { forwardRef } from 'react'

import { AirwallexPresentationRunner } from './AirwallexPresentationRunner'
import { StripeElementsPresentationRunner } from './StripeElementsPresentationRunner'

import type {
  CheckoutPresentationRunnerDefinition,
  CheckoutPresentationRunnerHandle,
  CheckoutPresentationRunnerComponent,
  CheckoutPresentationRunnerProps,
} from './types'
import type {
  CheckoutPresentation as CheckoutPresentationContract,
  StripeElementsMethodPresentation,
} from '@checkout/core'

const runnerDefinitions = {
  stripe_elements: {
    flow: 'stripe_elements',
    variant: 'button',
    hidesExternalSubmit: () => true,
    requiresExternalSubmit: () => false,
    component: StripeElementsPresentationRunner,
  },
  airwallex_dropin: {
    flow: 'airwallex_dropin',
    variant: 'panel',
    hidesExternalSubmit: (method) => method.pm_id !== 'card',
    requiresExternalSubmit: (method) => method.pm_id === 'card',
    component: AirwallexPresentationRunner,
  },
} satisfies Record<
  'stripe_elements' | 'airwallex_dropin',
  CheckoutPresentationRunnerDefinition & {
    component: CheckoutPresentationRunnerComponent
  }
>

type EmbeddedCheckoutPresentation =
  | Exclude<CheckoutPresentationContract, { flow: 'hosted_redirect' }>
  | StripeElementsMethodPresentation

type CheckoutPresentationRunnerRegistration = CheckoutPresentationRunnerDefinition & {
  component: CheckoutPresentationRunnerComponent
}

function resolveCheckoutPresentationRunner(
  presentation: CheckoutPresentationContract | StripeElementsMethodPresentation | null | undefined
): CheckoutPresentationRunnerRegistration | null {
  if (!presentation || presentation.mode !== 'embedded') {
    return null
  }
  const definition = runnerDefinitions[presentation.flow]
  if (!definition) {
    throw new Error('Checkout presentation runner is unavailable')
  }
  return definition
}

export function getCheckoutPresentationRunnerDefinition(
  presentation: CheckoutPresentationContract | StripeElementsMethodPresentation | null | undefined
): CheckoutPresentationRunnerDefinition | null {
  return resolveCheckoutPresentationRunner(presentation)
}

export function isEmbeddedCheckoutPresentation(
  presentation: CheckoutPresentationContract | StripeElementsMethodPresentation | null | undefined
): presentation is EmbeddedCheckoutPresentation {
  return resolveCheckoutPresentationRunner(presentation) !== null
}

export const CheckoutPresentationRunner = forwardRef<
  CheckoutPresentationRunnerHandle,
  CheckoutPresentationRunnerProps
>(function CheckoutPresentationRunner(props, ref) {
  const presentation = props.methodPresentation ?? props.execution?.presentation
  if (!presentation || presentation.flow === 'hosted_redirect') {
    throw new Error('Embedded checkout presentation is required')
  }
  const definition = resolveCheckoutPresentationRunner(presentation)
  if (!definition) {
    throw new Error('Checkout presentation runner is unavailable')
  }
  const Runner = definition.component
  return <Runner ref={ref} {...props} />
})
